import { useSyncExternalStore } from 'react';

/**
 * Georeferenced map plates — scanned sheets warped onto the map as rasters.
 *
 * Offered only when a WMS endpoint is configured: a dev server (the local
 * GeoServer), or REACT_APP_PLATES_WMS in a deployed build. Prod GeoServer has
 * served both mosaics since 2026-10-08 (gs://goldfinder-plates-syd mounted on
 * goldfinder-db), but ne:anumber_report_plates is read-restricted there to
 * PLATES/ADMIN and the browser calls GeoServer anonymously - so a deployed
 * build lists only the sources REACT_APP_PLATES_SOURCES names (e.g. "asx").
 *
 * Two provenance streams, deliberately kept apart (LAYERS.md §8):
 *   - ne:asx_plates             figures from ASX company announcements
 *   - ne:anumber_report_plates  sheets from statutory WAMEX A-number reports
 */
export const PLATES_WMS = process.env.REACT_APP_PLATES_WMS
  || (process.env.NODE_ENV === 'development' ? 'http://localhost:8600/geoserver/ne/wms' : null);

/**
 * The read-restricted A-number mosaic through the API, for admins only
 * (gold-finder-api routes/layers.js - signs in to GeoServer as a read-only proxy
 * user; the browser holds an admin session cookie, see useAdminSession). Unset:
 * the A-number source uses PLATES_WMS like ASX, which is right for the local
 * GeoServer, where the rules are open.
 */
export const PLATES_RESTRICTED_WMS = process.env.REACT_APP_PLATES_RESTRICTED_WMS || null;

/** True when the plates come from the local GeoServer, not a deployed one. */
const PLATES_LOCAL = !!PLATES_WMS && /\/\/(localhost|127\.0\.0\.1)[:/]/.test(PLATES_WMS);
const localTag = PLATES_LOCAL ? ' (local)' : '';

/**
 * Which sources to offer: comma list of PLATE_LAYERS tabs, lower case ("asx",
 * "a-number"). Unset = all of them, which is right for the local GeoServer.
 */
const PLATES_SOURCES = (process.env.REACT_APP_PLATES_SOURCES || '')
  .split(',').map(s => s.trim().toLowerCase()).filter(Boolean);

const wfsOf = (wms) => (wms ? wms.replace(/\/wms\/?$/, '/wfs') : null);

/** Where the plate rasters sit: above base maps and DEA/ASTER, below vectors. */
export const PLATES_BEFORE_ID = 'anchor-plates-top';

/**
 * Below this zoom no plate raster is drawn. The sheets are 1:10k-1:50k; zoomed
 * further out, hundreds overlap into noise and each is its own tile request.
 * The plate list still works at any zoom.
 */
export const PLATES_MIN_ZOOM = 9;

/**
 * Most plate rasters mounted at once (MapLibre sources). Plates in view are
 * kept first; beyond them the least recently wanted are unmounted. When more
 * plates are shown in view than this, the best-placed ones are drawn.
 */
export const PLATES_MAX_MOUNTED = 60;

/* ----------------------------------------------------------------------------
 * Error bound: how far a point on the plate may sit from where it truly is.
 * err_m = fit error + a datum allowance (about 210 m where the sheet does not
 * print its datum and AMG vs MGA cannot be told apart). Shown whenever a plate
 * is listed or drawn, never behind a click.
 * ------------------------------------------------------------------------- */

const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** The plate's error bound in metres, or null. Old rows fall back to their fit residual. */
export function errOf(p) {
  return num(p?.err_m) ?? num(p?.residual_m);
}

/** ±40 m, ±1.2 km, ±11 km. */
export function formatErr(m) {
  if (m === null || m === undefined) return '± ?';
  if (m < 1000) return `±${Math.round(m)} m`;
  return `±${m < 10000 ? (m / 1000).toFixed(1) : Math.round(m / 1000)} km`;
}

/** 'good' (≤50 m) | 'fair' (≤150 m) | 'poor' (>150 m) | 'unknown'. */
export function errBand(m) {
  if (m === null || m === undefined) return 'unknown';
  if (m <= 50) return 'good';
  if (m <= 150) return 'fair';
  return 'poor';
}

/** Band colours: text on white, used for the list badge, the map label and the outline. */
export const ERR_BAND_COLOURS = {
  good: '#1B7A3A',
  fair: '#A65E00',
  poor: '#B3261E',
  unknown: '#4A5558',
};

function metres(v) {
  const m = num(v);
  return m === null ? null : `${Math.round(m)} m`;
}

/** datum_basis in plain words (vocabulary: place_anumber.py, place_asx.py, sql/060). */
const DATUM_WORDS = {
  sheet_amg: 'AMG printed on the sheet',
  sheet_amg_agd_unresolved: 'AMG printed on the sheet (AGD66/84 unresolved)',
  sheet_gda94: 'MGA / GDA94 printed on the sheet',
  sheet_gda2020: 'GDA2020 printed on the sheet',
  printed_amg: 'AMG printed',
  printed_mga: 'MGA printed',
  era_amg_pre1994: 'datum not printed; AMG (pre-1994 report)',
  assumed_amg_era: 'datum not printed; AMG assumed',
  assumed_mga_era: 'datum not printed; MGA assumed (post-2005)',
  ambiguous_amg_in_announcement: 'datum ambiguous (announcement mentions AMG)',
  announcement: 'datum from the announcement',
  assumed: 'datum not printed; MGA assumed',
};

export function datumWords(basis) {
  if (!basis) return null;
  return DATUM_WORDS[basis] || String(basis).replace(/_/g, ' ');
}

/** "±250 m (fit 40 m + datum 210 m)". */
function errDetail(p) {
  const m = errOf(p);
  if (m === null) return p?.placed === 'hand' ? 'none recorded (placed by hand)' : null;
  const fit = num(p.err_fit_m);
  const datum = num(p.err_datum_m);
  const parts = [fit !== null && `fit ${Math.round(fit)} m`, datum !== null && `datum ${Math.round(datum)} m`]
    .filter(Boolean);
  const basis = num(p.err_m) === null ? ' (fit residual only)' : '';
  return `${formatErr(m)}${parts.length ? ` (${parts.join(' + ')})` : ''}${basis}`;
}

/** Tooltip for the badge and label: what the number is, and what is in it. */
export function errTitle(p) {
  const m = errOf(p);
  if (m === null) {
    return p?.placed === 'hand'
      ? 'Placement error bound: none recorded for this hand-placed plate.'
      : 'Placement error bound: none recorded.';
  }
  if (num(p.err_m) === null) {
    return `Placement error: fit residual ${formatErr(m)} only. No full error bound (no datum allowance) recorded yet.`;
  }
  const datum = num(p.err_datum_m);
  const datumNote = datum !== null
    ? ` Includes a ${Math.round(datum)} m datum allowance${datum >= 200 ? ' because the datum is not printed' : ''}.`
    : ' Where the datum is not printed, it includes an allowance of about 210 m.';
  const words = datumWords(p.datum_basis);
  return `Placement error bound ${formatErr(m)}: how far a point on this plate may sit from its true position.`
    + `${datumNote}${words ? ` Datum basis: ${words}.` : ''}`
    + ' Good ≤50 m, fair ≤150 m, poor >150 m.';
}

/** Shown when nobody has said otherwise. A row without default_on is shown (older index). */
export function defaultOn(p) {
  const d = p?.default_on;
  if (d === null || d === undefined || d === '') return true;
  return Number(d) === 1 || d === true || d === 'true';
}

/**
 * One entry per sidebar checkbox. `key` is the overlayVisibility flag.
 *
 * ASX: an ImageMosaic that must always be sent a filter and a sort. The FIRST
 * granule in sort order draws on top, so `z_order D` puts the fine data plates
 * over the coarse, fully opaque location maps (LAYERS.md §8, re-measured
 * 2026-09-25: `z_order A` returns the location map alone).
 */
const ALL_PLATE_LAYERS = [
  {
    key: 'asxPlates',
    id: 'plates-asx',
    label: `ASX plates${localTag}`,
    title: 'Figures from ASX company announcements — company disclosure, not the WAMEX record. Click a plate to identify it.',
    wms: PLATES_WMS,
    layer: 'ne:asx_plates',
    params: { CQL_FILTER: 'INCLUDE', sortBy: 'z_order D' },
    index: 'ne:asx_plates_index',
    tab: 'ASX',
    // The mosaic attribute a single plate is selected by.
    idField: 'plate_id',
    // Columns the UI reads; the index request asks for these and the geometry
    // only (those the layer actually has, see loadPlateIndex).
    props: ['location', 'plate_id', 'title', 'ticker', 'company', 'asx_id', 'plate_date', 'project',
      'figure', 'res_m', 'z_order', 'src_crs', 'err_m', 'err_fit_m', 'err_datum_m', 'datum_basis',
      'placed', 'default_on'],
    listItem: (p) => ({
      title: p.title || p.plate_id,
      source: [p.ticker && `ASX:${p.ticker}`, p.plate_date, p.figure].filter(Boolean).join(' · '),
      sub: p.company,
    }),
    // Coarse, opaque location maps low; fine data plates on top.
    order: (p) => Number(p.z_order) || 0,
    describe: (p) => ({
      heading: p.title || p.plate_id,
      rows: [
        ['Error bound', errDetail(p)],
        ['Datum', datumWords(p.datum_basis)],
        ['Company', [p.company, p.ticker && `(ASX:${p.ticker})`].filter(Boolean).join(' ')],
        ['Announcement', [p.plate_date, p.figure].filter(Boolean).join(' · ')],
        ['Project', p.project],
        ['Resolution', p.res_m && `${Number(p.res_m).toFixed(1)} m/px (native)`],
        ['Plate', p.plate_id],
      ],
      link: p.asx_id ? {
        // The same displayAnnouncement.do / idsId link asx-recon/asx.py scrapes.
        href: `https://www.asx.com.au/asx/v2/statistics/displayAnnouncement.do?display=pdf&idsId=${p.asx_id}`,
        text: `ASX ${p.asx_id}`,
      } : null,
    }),
  },
  {
    key: 'anumberPlates',
    id: 'plates-anumber',
    label: `A-number report plates${localTag}`,
    title: 'Georeferenced sheets from WAMEX A-number reports. Proof-of-concept placements. Click a plate to identify it.',
    wms: PLATES_RESTRICTED_WMS || PLATES_WMS,
    // Needs the admin session before any request (useAdminSession).
    restricted: !!PLATES_RESTRICTED_WMS,
    layer: 'ne:anumber_report_plates',
    params: {},
    index: 'ne:anumber_report_plates_footprints',
    tab: 'A-number',
    // No plate id in the granule index; the granule's own path is unique.
    idField: 'location',
    // Everything but georef_json, a large text column on the hand-placed rows
    // that nothing here reads.
    props: ['anumber', 'plate', 'location', 'plate_id', 'status', 'residual_m', 'control_ties',
      'crs_source', 'measured', 'err_m', 'err_fit_m', 'err_datum_m', 'datum_basis', 'default_on',
      'placed', 'year', 'res_m'],
    listItem: (p) => ({
      title: `${p.anumber} — plate ${p.plate}`,
      source: [`WAMEX ${p.anumber}`, p.year, datumWords(p.datum_basis)].filter(Boolean).join(' · '),
      sub: [p.placed && `${p.placed === 'hand' ? 'Hand' : 'Auto'}-placed`, p.status].filter(Boolean).join(' · '),
    }),
    // The most trustworthy placement on top: smallest error bound uppermost,
    // finer resolution breaking ties. Where two sheets overlap, the one drawn
    // is the one whose position can be relied on. The two hand-placed proof
    // sheets fall back to their fit residual until they carry an err_m.
    order: (p) => -(errOf(p) ?? 1e6) - (Number(p.res_m) || 0) / 1e4,
    describe: (p) => ({
      heading: `${p.anumber} — plate ${p.plate}`,
      rows: [
        ['Error bound', errDetail(p)],
        ['Fit error', metres(p.err_fit_m)],
        ['Datum allowance', metres(p.err_datum_m)],
        ['Datum', datumWords(p.datum_basis)],
        ['Report year', p.year],
        ['Placed', p.placed && (p.placed === 'hand' ? 'by hand' : 'automatically')],
        ['Resolution', p.res_m && `${Number(p.res_m).toFixed(1)} m/px`],
        ['Fit residual', p.residual_m && `${p.residual_m} m (${p.control_ties} ties)`],
        ['Source CRS', p.crs_source],
        ['Status', p.status],
        ['Measured', p.measured],
      ],
      link: p.anumber ? {
        href: `https://wamex.dmp.wa.gov.au/Wamex/Search/ReportDetails?ANumber=${String(p.anumber).replace(/^A/i, '')}`,
        text: `WAMEX ${p.anumber}`,
      } : null,
    }),
  },
];

export const PLATE_LAYERS = PLATES_SOURCES.length
  ? ALL_PLATE_LAYERS.filter(l => PLATES_SOURCES.includes(l.tab.toLowerCase()))
  : ALL_PLATE_LAYERS;

export const PLATES_AVAILABLE = !!PLATES_WMS && PLATE_LAYERS.length > 0;

/** Whether any offered source comes through the API and needs the admin session. */
export const PLATES_NEED_SESSION = PLATE_LAYERS.some((l) => l.restricted);

/** Deepest tile requested; MapLibre over-zooms past it. */
const PLATES_MAXZOOM = 17;

/* ----------------------------------------------------------------------------
 * One plate = one footprint feature from a layer's index.
 * ------------------------------------------------------------------------- */

/** Stable key for a plate across reloads: layer key + the mosaic id field. */
export const plateKey = (layer, props) => `${layer.key}:${props[layer.idField]}`;

// Footprints never change once loaded; 3,300 of them are filtered on every map move.
const boundsCache = new WeakMap();

/** [west, south, east, north] of a footprint, in lng/lat. */
export function featureBounds(feature) {
  if (feature && boundsCache.has(feature)) return boundsCache.get(feature);
  const b = computeBounds(feature);
  if (feature && typeof feature === 'object') boundsCache.set(feature, b);
  return b;
}

function computeBounds(feature) {
  const g = feature?.geometry;
  const polys = g?.type === 'Polygon' ? [g.coordinates] : g?.type === 'MultiPolygon' ? g.coordinates : [];
  let w = Infinity; let s = Infinity; let e = -Infinity; let n = -Infinity;
  polys.forEach((rings) => rings[0].forEach(([x, y]) => {
    if (x < w) w = x; if (x > e) e = x; if (y < s) s = y; if (y > n) n = y;
  }));
  return Number.isFinite(w) ? [w, s, e, n] : null;
}

/** Whether two [w, s, e, n] boxes overlap. */
export const boundsIntersect = (a, b) => !!(a && b && a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1]);

/** A box grown by a fraction of its size on every side. */
export const padBounds = ([w, s, e, n], f) => {
  const dx = (e - w) * f; const dy = (n - s) * f;
  return [w - dx, s - dy, e + dx, n + dy];
};

/**
 * Where a plate's error label sits: the footprint vertex nearest the north-west
 * corner of its bounds, i.e. the sheet's top-left corner even when rotated.
 */
export function labelAnchor(feature) {
  const b = featureBounds(feature);
  if (!b) return null;
  const g = feature.geometry;
  const polys = g.type === 'Polygon' ? [g.coordinates] : g.coordinates;
  let best = [b[0], b[3]]; let d = Infinity;
  polys.forEach((rings) => rings[0].forEach(([x, y]) => {
    const dd = (x - b[0]) ** 2 + (y - b[3]) ** 2;
    if (dd < d) { d = dd; best = [x, y]; }
  }));
  return best;
}

const cqlFor = (layer, props) => `${layer.idField}='${String(props[layer.idField]).replace(/'/g, "''")}'`;

const getMapUrl = (layer, props, rest) => `${layer.wms}?service=WMS&version=1.1.1&request=GetMap`
  + `&layers=${encodeURIComponent(layer.layer)}&styles=`
  // Always a filter, never the whole mosaic: unfiltered, the mosaic stacks every
  // granule and a coarse opaque sheet can hide a fine one (LAYERS.md §8).
  + `&CQL_FILTER=${encodeURIComponent(cqlFor(layer, props))}`
  + `&transparent=true&format=image/png${rest}`;

// Built once per plate: a fresh `tiles` array on a re-render would make
// react-map-gl tear the source down and refetch every tile.
const sourceCache = new Map();

/**
 * One raster source per plate, filtered to that plate and bounded to its
 * footprint, so MapLibre never asks for a tile the plate does not touch. That
 * is what makes per-plate show/hide and opacity cheap: each is its own layer.
 */
export function buildPlateSource(layer, feature) {
  const key = plateKey(layer, feature.properties);
  if (!sourceCache.has(key)) {
    const bounds = featureBounds(feature);
    sourceCache.set(key, {
      type: 'raster',
      tileSize: 256,
      maxzoom: PLATES_MAXZOOM,
      ...(bounds ? { bounds } : null),
      // GetMap renders from the mosaic on every request (no GWC), so a plate
      // added with build_mosaic.py shows after a GeoServer restart and reload.
      tiles: [getMapUrl(layer, feature.properties,
        '&bbox={bbox-epsg-3857}&srs=EPSG:3857&width=256&height=256')],
    });
  }
  return sourceCache.get(key);
}

/**
 * A small picture of one plate: GetMap over its own footprint, sized to the
 * footprint's shape. WMS 1.1.1 with EPSG:4326 takes the bbox as lng,lat.
 */
export function plateThumbnailUrl(layer, feature, maxPx = 192) {
  const b = featureBounds(feature);
  if (!b) return null;
  const [w, s, e, n] = b;
  // Degrees of longitude shrink with latitude; without this the picture is
  // stretched east-west.
  const aspect = ((e - w) * Math.cos((((s + n) / 2) * Math.PI) / 180)) / (n - s || 1e-9);
  const width = aspect >= 1 ? maxPx : Math.max(16, Math.round(maxPx * aspect));
  const height = aspect >= 1 ? Math.max(16, Math.round(maxPx / aspect)) : maxPx;
  return getMapUrl(layer, feature.properties,
    `&bbox=${w},${s},${e},${n}&srs=EPSG:4326&width=${width}&height=${height}`);
}

/* ----------------------------------------------------------------------------
 * Shared state between the sidebar, the plate list and the map overlay:
 * per-plate visibility and opacity (a per-browser preference, not part of
 * presets) and the footprint index each layer's WFS serves.
 * ------------------------------------------------------------------------- */

const PREFS_KEY = 'goldfinder_plate_prefs';
export const PLATES_DEFAULT_OPACITY = 0.85;

const ORDER_KEY = 'goldfinder_plate_order';
const FILTER_KEY = 'goldfinder_plate_filters';

let state = {
  prefs: readPrefs(),
  index: {},
  // Plate keys the user has stacked by hand, top of the stack first.
  order: readJson(ORDER_KEY, []),
  // Per layer key: { from, to, undated } - a year range for list and map.
  filters: readJson(FILTER_KEY, {}),
};
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };

function readPrefs() {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; }
}

function readJson(key, fallback) {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    return v && typeof v === 'object' ? v : fallback;
  } catch { return fallback; }
}

function save(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ }
}

/** Patch one or more plates' prefs: { [plateKey]: { visible?, opacity? } }. */
export function setPlatePrefs(patch) {
  const prefs = { ...state.prefs };
  Object.entries(patch).forEach(([k, v]) => { prefs[k] = { ...prefs[k], ...v }; });
  state = { ...state, prefs };
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* private mode */ }
  emit();
}

export function usePlatesState() {
  return useSyncExternalStore(subscribe, () => state);
}

/** Forget the saved show/hide of these plates, so each goes back to its default_on. */
export function resetPlateVisibility(keys) {
  const prefs = { ...state.prefs };
  keys.forEach((k) => {
    if (!prefs[k]) return;
    const { visible, ...rest } = prefs[k]; // eslint-disable-line no-unused-vars
    if (Object.keys(rest).length) prefs[k] = rest; else delete prefs[k];
  });
  state = { ...state, prefs };
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* private mode */ }
  emit();
}

/**
 * Whether a plate is shown: its saved preference if there is one, else the
 * index's default_on (missing = shown). Opacity is a preference only.
 */
export function plateVisible(s, layer, feature) {
  const v = s.prefs[plateKey(layer, feature.properties)]?.visible;
  return typeof v === 'boolean' ? v : defaultOn(feature.properties);
}
export const plateOpacity = (s, key) => s.prefs[key]?.opacity ?? PLATES_DEFAULT_OPACITY;

/* ----------------------------------------------------------------------------
 * Date filter. A year range per layer, applied to the list AND to what is
 * drawn - a plate outside the range is neither listed nor on the map, whatever
 * its show/hide. It does not touch show/hide, so clearing it brings back
 * exactly what was there. ASX plates are dated by announcement, A-number
 * plates by report year.
 * ------------------------------------------------------------------------- */

export function plateYear(layer, p) {
  if (layer.key === 'asxPlates') {
    const y = parseInt(String(p.plate_date || '').slice(0, 4), 10);
    return Number.isFinite(y) ? y : null;
  }
  const y = parseInt(p.year, 10);
  return Number.isFinite(y) ? y : null;
}

const NO_FILTER = { from: null, to: null, undated: true };
export const plateFilter = (s, layer) => ({ ...NO_FILTER, ...s.filters[layer.key] });
export const filterActive = (f) => f.from != null || f.to != null;

export function setPlateFilter(layerKey, patch) {
  const filters = { ...state.filters, [layerKey]: { ...NO_FILTER, ...state.filters[layerKey], ...patch } };
  state = { ...state, filters };
  save(FILTER_KEY, filters);
  emit();
}

export function inDateRange(s, layer, props) {
  const f = plateFilter(s, layer);
  if (!filterActive(f)) return true;
  const y = plateYear(layer, props);
  if (y == null) return f.undated;
  return (f.from == null || y >= f.from) && (f.to == null || y <= f.to);
}

/** Drawn on the map: shown (plateVisible) and inside the layer's date range. */
export const plateDrawn = (s, layer, feature) => plateVisible(s, layer, feature)
  && inDateRange(s, layer, feature.properties);

/* ----------------------------------------------------------------------------
 * Stacking by hand. `order` holds the plates the user has moved, top first;
 * within a layer they draw above every plate not in it, in that order. The rest
 * keep the layer's own order. Layers keep their rank: ASX under A-number.
 * ------------------------------------------------------------------------- */

const orderIndexCache = new WeakMap();
function orderIndex(s) {
  if (!orderIndexCache.has(s.order)) orderIndexCache.set(s.order, new Map(s.order.map((k, i) => [k, i])));
  return orderIndexCache.get(s.order);
}
export const orderedByHand = (s, key) => orderIndex(s).has(key);

/** compareDraw that honours the user's stacking. Bottom to top, like compareDraw. */
export function compareDrawIn(s) {
  const idx = orderIndex(s);
  return (la, pa, lb, pb) => {
    const r = drawRank(la) - drawRank(lb);
    if (r) return r;
    const ia = idx.get(plateKey(la, pa));
    const ib = idx.get(plateKey(lb, pb));
    if (ia != null && ib != null) return ib - ia; // smaller index = higher
    if (ia != null) return 1;
    if (ib != null) return -1;
    return la.order(pa) - lb.order(pb);
  };
}

/**
 * Move one plate within a stack. `stack` is the plates as the list shows them,
 * top first; `to` is 'up', 'down', 'top' or 'bottom'. Every plate in the stack
 * is written to `order` in its new position, so the whole visible stack is
 * pinned the way the user sees it; plates ordered earlier elsewhere keep their
 * place after it.
 */
export function movePlate(key, stack, to) {
  const i = stack.indexOf(key);
  if (i < 0) return;
  const next = stack.filter((k) => k !== key);
  const j = { up: i - 1, down: i + 1, top: 0, bottom: next.length }[to];
  if (j == null) return;
  next.splice(Math.max(0, Math.min(next.length, j)), 0, key);
  const rest = state.order.filter((k) => !next.includes(k));
  const order = [...next, ...rest];
  state = { ...state, order };
  save(ORDER_KEY, order);
  emit();
}

/** Forget the hand stacking of these plates (all, when `keys` is omitted). */
export function resetPlateOrder(keys) {
  const drop = keys ? new Set(keys) : null;
  const order = drop ? state.order.filter((k) => !drop.has(k)) : [];
  state = { ...state, order };
  save(ORDER_KEY, order);
  emit();
}

/**
 * Draw order across both layers, bottom to top: every ASX plate under every
 * A-number plate (PLATE_LAYERS order, as before), then each layer's `order`.
 */
export const drawRank = (layer) => PLATE_LAYERS.indexOf(layer);
export function compareDraw(la, pa, lb, pb) {
  return (drawRank(la) - drawRank(lb)) || (la.order(pa) - lb.order(pb));
}

/**
 * Which plate rasters to keep mounted: a small LRU over plate keys, most
 * recently wanted last. `wanted` is the plates to draw now, most important
 * first; `alive(key)` says whether a key still exists (its layer on, in the
 * index). Everything wanted is kept (up to `cap`); the rest of the slots go to
 * the most recently wanted of the others, so panning back refetches nothing.
 * Returns `prev` itself when nothing changed, so it is safe as React state.
 */
export function selectMounted(prev, wanted, cap, alive = () => true) {
  const want = wanted.slice(0, cap);
  const wantSet = new Set(want);
  const kept = prev.filter((k) => !wantSet.has(k) && alive(k));
  const all = [...kept, ...want];
  const next = all.slice(Math.max(0, all.length - cap));
  return next.length === prev.length && next.every((k, i) => k === prev[i]) ? prev : next;
}

/**
 * Fetch a layer's footprint index once. Status: 'loading' | 'ok' | 'denied' |
 * 'error'. 'denied' is GeoServer's answer to an anonymous caller for a
 * restricted layer under mode=HIDE: an OGC exception saying the type is
 * unknown. WFS 1.0.0 keeps coordinates in lng,lat whatever the declared CRS.
 */
export function loadPlateIndex(plate) {
  const current = state.index[plate.key];
  if (current && current.status !== 'error') return;
  const put = (v) => { state = { ...state, index: { ...state.index, [plate.key]: v } }; emit(); };
  put({ status: 'loading', features: [] });
  const base = `${wfsOf(plate.wms)}?service=WFS&version=1.0.0`
    + `&typeName=${encodeURIComponent(plate.index)}`;
  const refused = (text) => (/unknown|LayerNotDefined|Could not find/i.test(text) ? 'denied' : 'error');
  // Ask for the listed columns only, and only those the layer has: GeoServer
  // rejects the whole request over one unknown propertyName, and the index
  // gains columns as the placement pipeline grows. Without a list, everything.
  const columns = plate.props
    ? fetch(`${base}&request=DescribeFeatureType`).then((r) => r.text()).then((xsd) => {
      if (/ServiceException/.test(xsd)) throw Object.assign(new Error('describe'), { status: refused(xsd) });
      return propertyNameFor(xsd, plate.props);
    })
    : Promise.resolve(null);
  columns
    .then((names) => fetch(`${base}&request=GetFeature&outputFormat=application/json`
      + (names ? `&propertyName=${encodeURIComponent(names.join(','))}` : '')))
    .then((r) => r.text())
    .then((text) => {
      if (text.trimStart().startsWith('<')) {
        put({ status: refused(text), features: [] });
        return;
      }
      put({ status: 'ok', features: (JSON.parse(text).features || []).filter((f) => f.geometry) });
    })
    .catch((err) => put({ status: err?.status || 'error', features: [] }));
}

/**
 * The propertyName list for a GetFeature: the geometry column plus each wanted
 * column the DescribeFeatureType XSD declares. Null when the XSD names no
 * geometry (then the request goes without a list and gets everything).
 */
export function propertyNameFor(xsd, wanted) {
  const els = [...xsd.matchAll(/<xsd:element\b[^>]*\bname="([^"]+)"[^>]*\btype="([^"]+)"/g)]
    .map(([, name, type]) => ({ name, type }));
  const geom = els.find((e) => /^gml:/.test(e.type));
  if (!geom) return null;
  const have = new Set(els.map((e) => e.name));
  return [geom.name, ...wanted.filter((n) => have.has(n))];
}

/** Ray-casting point-in-polygon over a GeoJSON Polygon / MultiPolygon. */
function inRing(x, y, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function featureContains(feature, lng, lat) {
  const g = feature?.geometry;
  if (!g) return false;
  const polys = g.type === 'Polygon' ? [g.coordinates] : g.type === 'MultiPolygon' ? g.coordinates : [];
  return polys.some((rings) => inRing(lng, lat, rings[0])
    && !rings.slice(1).some((hole) => inRing(lng, lat, hole)));
}
